import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TakeQuizModal from './components/TakeQuizModal';
import { getQuizzesForTrainee } from '../../api_service';

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock'       },
  COMPLETED: { label: 'Completed', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle' },
};

const MyQuizzes = () => {
  const navigate = useNavigate();
  const traineeId = sessionStorage.getItem('userId');
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [takeTarget, setTakeTarget] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadQuizzes(); }, []);

  const loadQuizzes = async () => {
    setLoading(true);
    try {
      const res = await getQuizzesForTrainee(traineeId);
      setQuizzes(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setQuizzes([]);
    } finally {
      setLoading(false);
    }
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error bg-error/10' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning bg-warning/10' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning bg-warning/10' };
    return { text: `${diff} days left`, cls: 'text-muted-foreground bg-muted/40' };
  };

  const statusOf = (item) => item.attempt?.status || 'PENDING';

  const filtered = quizzes.filter(item => {
    if (filter === 'all') return true;
    return statusOf(item) === filter;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="trainee"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">My Quizzes</h1>
          <p className="text-sm text-muted-foreground mt-1">Take your assigned assessments and view your scores</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            { key: 'all',       label: 'Total',     icon: 'FileQuestion', color: 'text-primary' },
            { key: 'PENDING',   label: 'Pending',   icon: 'Clock',        color: 'text-warning' },
            { key: 'COMPLETED', label: 'Completed', icon: 'CheckCircle',  color: 'text-success' },
          ].map(s => {
            const count = s.key === 'all' ? quizzes.length
              : quizzes.filter(q => statusOf(q) === s.key).length;
            return (
              <button
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`bg-card border rounded-xl p-4 text-left transition-all ${filter === s.key ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/40'}`}
              >
                <Icon name={s.icon} size={20} className={`${s.color} mb-2`} />
                <p className="text-2xl font-bold text-foreground">{count}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </button>
            );
          })}
        </div>

        {/* Quiz list */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading quizzes...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No quizzes found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(item => {
              const status = statusOf(item);
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
              const dl = deadlineLabel(item.quiz?.deadline);
              return (
                <div key={item.quiz?.quizId} className="bg-card border border-border rounded-xl p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-foreground">{item.quiz?.title}</h3>
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {status === 'PENDING' && dl && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${dl.cls}`}>{dl.text}</span>
                        )}
                      </div>
                      {item.quiz?.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.quiz.description}</p>
                      )}
                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{item.quiz?.questions?.length || 0} questions</span>
                        <span>Total marks: {item.quiz?.totalMarks}</span>
                        <span>Deadline: {item.quiz?.deadline || 'None'}</span>
                        {item.quiz?.timeLimitMinutes && <span>{item.quiz.timeLimitMinutes} min</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {status === 'PENDING' ? (
                        <Button size="sm" onClick={() => setTakeTarget(item)} iconName="Play" iconSize={14}>
                          Take Quiz
                        </Button>
                      ) : (
                        <span className="text-2xl font-bold text-success">
                          {item.attempt?.score ?? 0}/{item.quiz?.totalMarks}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {takeTarget && (
        <TakeQuizModal
          item={takeTarget}
          traineeId={traineeId}
          onClose={() => setTakeTarget(null)}
          onSubmitted={loadQuizzes}
        />
      )}
    </div>
  );
};

export default MyQuizzes;
